import React from 'react';
import { Check, Trash2, Edit3, Clock, MessageCircle, Gift, Repeat } from 'lucide-react';
import { CATEGORIES } from '../lib/theme.js';
import { formatDate, describeRepeat } from '../lib/dates.js';

export function TaskRow({ theme, task, currentUserId, partner, onComplete, onDelete, onEdit, watchOnly, noComplete }) {
  const category = CATEGORIES.find(c => c.id === task.categoryId) || CATEGORIES[9];
  const isOwn = task.createdBy === currentUserId;
  const isAssigned = task.assignedTo === currentUserId;
  const isMyPrivate = task.list === 'private' && task.createdBy === currentUserId;
  const isReceivedFromPartner = task.list === 'sent' && task.assignedTo === currentUserId;
  const isShared = task.list === 'shared';
  const isSentByMe = task.list === 'sent' && task.createdBy === currentUserId && task.assignedTo !== currentUserId;

  const repeatLabel = describeRepeat(task);

  // Determine accent color
  let accentColor = theme.textMuted;
  if (isReceivedFromPartner) accentColor = theme.coral;
  else if (isShared) accentColor = theme.teal;
  else if (isSentByMe) accentColor = theme.gold;

  const dueLabel = task.deadline ? formatDate(task.deadline.split('T')[0]) : null;
  const isLate = task.deadline && new Date(task.deadline) < new Date();

  const canComplete = !watchOnly && !noComplete && (isMyPrivate || isReceivedFromPartner || isShared);

  return (
    <div style={{
      background: theme.bgCard,
      border: `1px solid ${theme.borderSubtle}`,
      borderLeft: `3px solid ${accentColor}`,
      borderRadius: 10,
      padding: '11px 12px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
    }}>
      {/* Complete button */}
      {canComplete ? (
        <button
          onClick={() => onComplete(task.id)}
          style={{
            width: 22, height: 22,
            borderRadius: '50%',
            border: `2px solid ${theme.textDim}`,
            background: 'transparent',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: 2,
          }}
          title="Markér som løst"
        />
      ) : (
        <div style={{
          width: 22, height: 22,
          borderRadius: '50%',
          border: `2px dashed ${theme.borderSubtle}`,
          flexShrink: 0,
          marginTop: 2,
        }} />
      )}

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13 }}>{category.emoji}</span>
          <span style={{ fontSize: 14, fontWeight: 500, color: theme.text }}>{task.title}</span>
          {repeatLabel && (
            <span style={{ fontSize: 10, color: theme.textDim, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <Repeat size={10} /> {repeatLabel}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
          {dueLabel && (
            <span style={{
              fontSize: 11,
              color: isLate ? '#EF4444' : theme.textMuted,
              display: 'inline-flex', alignItems: 'center', gap: 3,
            }}>
              <Clock size={11} /> {dueLabel}
            </span>
          )}
          {task.tagPoints && task.list === 'sent' && (
            <span style={{ fontSize: 11, color: accentColor }}>{task.tagPoints} point</span>
          )}
          {isReceivedFromPartner && partner && (
            <span style={{ fontSize: 11, color: theme.textMuted }}>fra {partner.name?.split(' ')[0]}</span>
          )}
          {isSentByMe && partner && (
            <span style={{ fontSize: 11, color: theme.textMuted }}>til {partner.name?.split(' ')[0]}</span>
          )}
        </div>

        {/* Message */}
        {task.message && (
          <div style={{
            marginTop: 6, padding: '6px 10px',
            background: theme.bgElevated,
            borderRadius: 8,
            fontSize: 12,
            fontStyle: 'italic',
            color: theme.textMuted,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <MessageCircle size={11} />
            {task.messageHidden ? 'Besked venter' : `"${task.message}"`}
          </div>
        )}

        {/* Reward */}
        {task.reward && (
          <div style={{
            marginTop: 6, padding: '6px 10px',
            background: theme.bgElevated,
            borderRadius: 8,
            fontSize: 12,
            color: theme.gold,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Gift size={11} />
            {task.rewardHidden && task.assignedTo === currentUserId
              ? 'Belønning venter'
              : `${task.rewardEmoji || '🎁'} ${task.reward}`}
          </div>
        )}
      </div>

      {/* Action buttons */}
      {!watchOnly && onEdit && isOwn && (
        <button
          onClick={() => onEdit(task)}
          style={iconBtn(theme)}
          title="Rediger"
        >
          <Edit3 size={13} />
        </button>
      )}
      {!watchOnly && onDelete && isOwn && (
        <button
          onClick={() => onDelete(task.id)}
          style={iconBtn(theme)}
          title="Slet"
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
}

function iconBtn(theme) {
  return {
    background: 'transparent',
    border: 'none',
    color: theme.textDim,
    cursor: 'pointer',
    padding: 5,
    borderRadius: 6,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}
